import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';

import type { NpcDefinition, UserNpcConfig } from '../../../types/npc';
import { getFirebaseDb } from '../client';
import { ensureDebugSocratesNpc } from './debugSeed';
import {
  duplicateNpcName,
  type UserNpcUpsertInput,
  toNpcDefinition,
  toUserNpcConfig,
  toUserNpcWritePayload,
} from './npcDocuments';

const mergeUserNpcUpsertInput = (
  current: UserNpcConfig,
  payload: Partial<UserNpcUpsertInput>,
): UserNpcUpsertInput => ({
  name: payload.name ?? current.name,
  title: payload.title ?? current.title,
  expertise: payload.expertise ?? current.expertise,
  backstory: payload.backstory ?? current.backstory,
  personality: payload.personality ?? current.personality,
  greeting: payload.greeting ?? current.greeting,
  avatarLayers: payload.avatarLayers ?? current.avatarLayers,
  spriteColors: payload.spriteColors ?? current.spriteColors,
  avatarMode: payload.avatarMode ?? current.avatarMode,
  customAvatarId:
    payload.customAvatarId === undefined ? current.customAvatarId : payload.customAvatarId,
  personalitySpec:
    payload.personalitySpec === undefined ? current.personalitySpec : payload.personalitySpec,
  llmProvider: payload.llmProvider ?? current.llmProvider,
  llmModel: payload.llmModel ?? current.llmModel,
  fallbackProviders: payload.fallbackProviders ?? current.fallbackProviders ?? [],
  temperature: payload.temperature ?? current.temperature,
  maxTokens: payload.maxTokens ?? current.maxTokens,
  visibility: payload.visibility ?? current.visibility,
  isActive: payload.isActive ?? current.isActive,
  agentConfig: payload.agentConfig === undefined ? current.agentConfig : payload.agentConfig,
  moodProfile: payload.moodProfile ?? current.moodProfile,
});

export const getNpcConfigs = async (viewerUid?: string): Promise<NpcDefinition[]> => {
  const db = getFirebaseDb();
  if (viewerUid) {
    await ensureDebugSocratesNpc(viewerUid).catch((error: unknown) => {
      console.warn('[firestore] Unable to seed debug Socrates NPC:', error);
    });
  }

  const systemQuery = query(collection(db, 'npcs'), orderBy('name', 'asc'));
  const publicUserNpcQuery = query(
    collection(db, 'user_npcs'),
    where('visibility', '==', 'public'),
    where('isActive', '==', true),
  );
  const ownUserNpcQuery = viewerUid
    ? query(
        collection(db, 'user_npcs'),
        where('ownerId', '==', viewerUid),
        where('isActive', '==', true),
      )
    : null;

  const [systemSnapshot, publicUserSnapshot, ownUserSnapshot] = await Promise.all([
    getDocs(systemQuery),
    getDocs(publicUserNpcQuery),
    ownUserNpcQuery ? getDocs(ownUserNpcQuery) : Promise.resolve(null),
  ]);

  const systemNpcs = systemSnapshot.docs.map((item) => {
    const data = item.data() as Record<string, unknown>;
    return {
      ...toNpcDefinition(item.id, data),
      source: 'system' as const,
    };
  });

  const userByDocId = new Map<string, NpcDefinition>();
  const ingestUserDoc = (docId: string, rawData: Record<string, unknown>): void => {
    const mapped = toNpcDefinition(docId, rawData);
    userByDocId.set(docId, {
      ...mapped,
      id: `user:${docId}`,
      source: 'user',
      sourceNpcId: docId,
      spriteId: mapped.spriteId || 'npc_user',
    });
  };

  publicUserSnapshot.docs.forEach((snapshotDoc) => {
    ingestUserDoc(snapshotDoc.id, snapshotDoc.data() as Record<string, unknown>);
  });
  ownUserSnapshot?.docs.forEach((snapshotDoc) => {
    ingestUserDoc(snapshotDoc.id, snapshotDoc.data() as Record<string, unknown>);
  });

  const userNpcs = [...userByDocId.values()];
  return [...systemNpcs, ...userNpcs].sort((left, right) => left.name.localeCompare(right.name));
};

export const getOwnedUserNpcs = async (uid: string): Promise<UserNpcConfig[]> => {
  const db = getFirebaseDb();
  const ownedQuery = query(
    collection(db, 'user_npcs'),
    where('ownerId', '==', uid),
    orderBy('updatedAt', 'desc'),
  );
  const snapshot = await getDocs(ownedQuery);

  if (snapshot.empty) {
    return [];
  }

  return snapshot.docs.map((item) =>
    toUserNpcConfig(item.id, item.data() as Record<string, unknown>),
  );
};

export const getUserNpcById = async (npcId: string): Promise<UserNpcConfig | null> => {
  const db = getFirebaseDb();
  const snapshot = await getDoc(doc(db, 'user_npcs', npcId));
  if (!snapshot.exists()) {
    return null;
  }

  return toUserNpcConfig(snapshot.id, snapshot.data() as Record<string, unknown>);
};

export const createUserNpc = async (uid: string, payload: UserNpcUpsertInput): Promise<string> => {
  const db = getFirebaseDb();
  const nextRef = doc(collection(db, 'user_npcs'));

  await setDoc(nextRef, {
    ...toUserNpcWritePayload(uid, payload),
    createdAt: serverTimestamp(),
  });

  return nextRef.id;
};

export const updateUserNpc = async (
  uid: string,
  npcId: string,
  payload: Partial<UserNpcUpsertInput>,
): Promise<void> => {
  const db = getFirebaseDb();
  const docRef = doc(db, 'user_npcs', npcId);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(docRef);
    if (!snapshot.exists()) {
      throw new Error('NPC not found.');
    }
    const data = snapshot.data() as Record<string, unknown>;
    if (data.ownerId !== uid) {
      throw new Error('NPC not editable by current user.');
    }
    const current = toUserNpcConfig(snapshot.id, data);
    const mergedPayload = mergeUserNpcUpsertInput(current, payload);

    transaction.update(docRef, toUserNpcWritePayload(uid, mergedPayload));
  });
};

export const deleteUserNpc = async (uid: string, npcId: string): Promise<void> => {
  const db = getFirebaseDb();
  const docRef = doc(db, 'user_npcs', npcId);

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(docRef);
    if (!snapshot.exists()) {
      throw new Error('NPC not found.');
    }
    const data = snapshot.data() as Record<string, unknown>;
    if (data.ownerId !== uid) {
      throw new Error('NPC not deletable by current user.');
    }
    transaction.delete(docRef);
  });
};

export const duplicateUserNpc = async (uid: string, npcId: string): Promise<string> => {
  const db = getFirebaseDb();
  const sourceRef = doc(db, 'user_npcs', npcId);
  const nextRef = doc(collection(db, 'user_npcs'));

  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(sourceRef);
    if (!snapshot.exists()) {
      throw new Error('NPC not found.');
    }
    const data = snapshot.data() as Record<string, unknown>;
    if (data.ownerId !== uid) {
      throw new Error('NPC not duplicable by current user.');
    }
    const current = toUserNpcConfig(snapshot.id, data);

    const duplicatePayload: UserNpcUpsertInput = {
      name: duplicateNpcName(current.name),
      title: current.title,
      expertise: current.expertise,
      backstory: current.backstory,
      personality: current.personality,
      greeting: current.greeting,
      avatarLayers: current.avatarLayers,
      spriteColors: current.spriteColors,
      avatarMode: current.avatarMode,
      customAvatarId: current.customAvatarId,
      personalitySpec: current.personalitySpec,
      llmProvider: current.llmProvider,
      llmModel: current.llmModel,
      fallbackProviders: current.fallbackProviders ?? [],
      temperature: current.temperature,
      maxTokens: current.maxTokens,
      visibility: current.visibility,
      isActive: false,
      moodProfile: current.moodProfile,
      agentConfig: current.agentConfig,
    };

    transaction.set(nextRef, {
      ...toUserNpcWritePayload(uid, duplicatePayload),
      createdAt: serverTimestamp(),
    });
  });

  return nextRef.id;
};
